import { IoMdCheckmarkCircleOutline, IoMdCloseCircleOutline } from 'react-icons/io';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
	setBillingType,
	setPlan,
} from '../../../store/reducers/subscriptionSlice/subscriptionSlice';
import useSubUrl from '../../../hooks/useSubUrl';
import '../subscription-plan.scss';

const PERIOD_LABELS = {
	monthly: 'Month',
	quarterly: 'Quarter',
	yearly: 'Year',
	annual: 'Year',
};

const PricingCard = ({
	plan,
	billing,
	billingDetails,
	changeType,
	currentPlanKey,
	onOpenFreeModal,
	onSelectPlan,
}) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { suburl: suburlParam } = useParams();
	const suburl = useSubUrl();
	const activeSuburl = suburlParam || suburl;

	// `changeType` describes the whole card, but a card covers BOTH billing
	// cycles. Prefer the per-cycle flag so that being on Enterprise Monthly
	// doesn't also mark Enterprise Yearly as current — which made it impossible
	// to switch billing cycle. Fall back to changeType when the API hasn't sent
	// the per-cycle flag (e.g. plan lists fetched without a subscription).
	const isCurrentPlan =
		billingDetails?.is_current !== undefined
			? billingDetails.is_current
			: changeType === 'Current Plan';

	const isDowngrade = changeType === 'Downgrade Plan';

	// Same plan group, different billing cycle — the user is on this plan but
	// not on this cycle, so it's selectable as a billing-cycle change.
	const isOtherCycleOfCurrentPlan = changeType === 'Current Plan' && !isCurrentPlan;

	const durationLabel =
		PERIOD_LABELS[billingDetails?.period?.toLowerCase()] ?? billingDetails?.period;

	const badgeLabel = isCurrentPlan
		? 'Current Plan'
		: isOtherCycleOfCurrentPlan
			? 'Change Billing Cycle'
			: changeType;

	const buttonText = (() => {
		if (isCurrentPlan) return 'Current Plan';
		if (isOtherCycleOfCurrentPlan) return `Switch to ${durationLabel}ly`;
		switch (plan.key) {
			case 'professional':
				return 'Go Professional';
			case 'enterprise':
				return 'Go Enterprise';
			default:
				return 'Get Started';
		}
	})();

	const handleClick = () => {
		if (isCurrentPlan) return;

		if (plan.key === 'free') {
			onOpenFreeModal?.(plan);
			return;
		}

		if (currentPlanKey && onSelectPlan) {
			onSelectPlan(plan, billingDetails, isDowngrade ? 'downgrade' : 'upgrade');
			return;
		}

		dispatch(
			setPlan({
				id: plan._id,
				key: plan.key,
				title: plan.name,
				description: plan.meta?.description,
				billing: plan.billing,
			}),
		);

		dispatch(setBillingType(billing));
		const checkoutPath = activeSuburl ? `/user/${activeSuburl}/checkout` : '/checkout';
		navigate(`${checkoutPath}?plan=${plan.key}&billing=${billing}&step=billing-details`);
	};

	return (
		<div className={`pricing-card ${isCurrentPlan ? 'current-plan' : ''}`}>
			<div className="current-badge">{badgeLabel}</div>
			<h5 className="title">{plan.name}</h5>
			<p className="desc">{plan.meta?.description}</p>
			<div className="price">
				{plan.custom ? (
					<p className="contact">Contact Us</p>
				) : (
					<div className="price-main">
						<div className="price-row">
							<span className="currency">₹</span>

							<span className="amount">
								{Number(billingDetails?.amount_without_gst_in_rupees ?? 0).toFixed(2)}
							</span>
						</div>

						<div className="price-info">
							<span className="duration">/{durationLabel}</span>

							<span className="gst">
								+ GST ₹{Number(billingDetails?.tax_amount_in_rupees ?? 0).toFixed(2)}
							</span>
						</div>
					</div>
				)}
			</div>

			<div className="features-wrapper">
				<ul className="features">
					{plan.display_features?.map((item, index) => {
						const isObject = typeof item === 'object' && item !== null;

						const label = isObject ? item.label : item;
						const display = isObject ? item.display : null;
						const available = isObject ? item.available : true;

						return (
							<li
								key={index}
								style={{ opacity: available ? 1 : 0.5 }}
							>
								{available ? (
									<IoMdCheckmarkCircleOutline
										className="icon"
										style={{ color: '#68C230' }}
									/>
								) : (
									<IoMdCloseCircleOutline
										className="icon"
										style={{ color: '#dc3545' }}
									/>
								)}

								<span>
									{display && display !== '✓' && display !== '✗' ? `${display} ${label}` : label}
								</span>
							</li>
						);
					})}
				</ul>
			</div>

			<div className="btn-group-custom">
				<button
					onClick={handleClick}
					className={`btn mt-0 w-100 ${isCurrentPlan ? 'btn-disabled' : 'btn-primary'}`}
					disabled={isCurrentPlan}
				>
					{buttonText}
				</button>

				<Link
					to="https://pharmnex-app.vercel.app/pricing"
					className="btn btn-primary w-100 mt-0"
				>
					Learn More
				</Link>
			</div>
		</div>
	);
};

export default PricingCard;
